import { motion } from "framer-motion";

// Anatomy-of-a-candlestick illustration.
const CandlesDiagram = ({ caption }) => (
  <svg viewBox="0 0 240 130" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
    {[...Array(4)].map((_, i) => (
      <line key={i} x1="0" y1={20 + i * 28} x2="240" y2={20 + i * 28} stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
    ))}
    {/* bullish candle */}
    <line x1="70" y1="18" x2="70" y2="112" stroke="#BACD1C" strokeWidth="1.5" />
    <rect x="60" y="46" width="20" height="46" fill="#BACD1C" />
    <text x="70" y="126" textAnchor="middle" className="fill-[#BACD1C]" fontSize="8" fontFamily="monospace">BULL</text>
    {/* bearish candle */}
    <line x1="150" y1="24" x2="150" y2="118" stroke="#f87171" strokeWidth="1.5" />
    <rect x="140" y="44" width="20" height="44" fill="#f87171" />
    <text x="150" y="126" textAnchor="middle" className="fill-red-400" fontSize="8" fontFamily="monospace">BEAR</text>
    {/* labels */}
    <text x="92" y="42" className="fill-white/40" fontSize="7" fontFamily="monospace">upper wick</text>
    <text x="92" y="70" className="fill-white/40" fontSize="7" fontFamily="monospace">body</text>
    <text x="92" y="100" className="fill-white/40" fontSize="7" fontFamily="monospace">lower wick</text>
  </svg>
);

// Support & resistance illustration.
const SRDiagram = ({ caption }) => (
  <svg viewBox="0 0 240 130" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
    <line x1="0" y1="28" x2="240" y2="28" stroke="#f87171" strokeWidth="1" strokeDasharray="4 4" opacity="0.7" />
    <line x1="0" y1="100" x2="240" y2="100" stroke="#BACD1C" strokeWidth="1" strokeDasharray="4 4" opacity="0.7" />
    <path d="M8,100 L45,32 L80,98 L120,30 L160,100 L200,34 L232,70" fill="none" stroke="#e5e7eb" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
    <text x="6" y="22" className="fill-red-400" fontSize="8" fontFamily="monospace">RESISTANCE</text>
    <text x="6" y="114" className="fill-[#BACD1C]" fontSize="8" fontFamily="monospace">SUPPORT</text>
  </svg>
);

// Generic pattern illustration driven by data.
const PatternDiagram = ({ d }) => {
  const color = d.bull === false ? "#f87171" : "#BACD1C";
  return (
    <svg viewBox="0 0 240 120" className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
      {[...Array(4)].map((_, i) => (
        <line key={`h${i}`} x1="0" y1={16 + i * 28} x2="240" y2={16 + i * 28} stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
      ))}
      {[...Array(7)].map((_, i) => (
        <line key={`v${i}`} x1={i * 40} y1="0" x2={i * 40} y2="120" stroke="rgba(255,255,255,0.04)" strokeWidth="0.5" />
      ))}
      {d.level != null && (
        <line x1="0" y1={d.level} x2="240" y2={d.level} stroke={color} strokeWidth="1" strokeDasharray="5 4" opacity="0.55" />
      )}
      <motion.path
        d={d.path}
        fill="none"
        stroke={color}
        strokeWidth="2.4"
        strokeLinejoin="round"
        strokeLinecap="round"
        initial={{ pathLength: 0, opacity: 0 }}
        whileInView={{ pathLength: 1, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.4, ease: "easeInOut" }}
      />
      {(d.points || []).map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="3.4" fill="#0A0A0A" stroke={color} strokeWidth="1.8" />
      ))}
      {d.breakout && <circle cx={d.breakout[0]} cy={d.breakout[1]} r="4.5" fill={color} />}
    </svg>
  );
};

export const LessonDiagram = ({ d }) => {
  if (!d) return null;
  const body =
    d.type === "candles" ? <CandlesDiagram caption={d.caption} /> :
    d.type === "sr" ? <SRDiagram caption={d.caption} /> :
    <PatternDiagram d={d} />;

  return (
    <figure data-testid="lesson-diagram" className="relative border border-white/12 bg-[#070707] overflow-hidden">
      <div className="flex items-center gap-2 border-b border-white/10 bg-[#111111] px-4 py-2.5">
        <span className="h-2 w-2 rounded-full bg-white/15" />
        <span className="h-2 w-2 rounded-full bg-white/15" />
        <span className="h-2 w-2 rounded-full bg-[#BACD1C]/60" />
        <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">figure.svg</span>
      </div>
      <div className="p-5">{body}</div>
      {d.caption && (
        <figcaption className="border-t border-white/10 px-4 py-2.5 font-mono text-[11px] text-white/40">{d.caption}</figcaption>
      )}
    </figure>
  );
};
